import { useQuery } from '@tanstack/react-query';

import { openApiFetch } from '@/lib/openapi-fetch';

import { buildClientStatusFilter, buildSupplyChainTypeFilter } from '../configs/supplierEvents';
import { supplychainKeys } from './keys';

import type { components } from '../../../../../unifiedapi';

type AggregationResult = components['schemas']['AggregationResult'];
export type PortfolioScoreResponse = components['schemas']['PortfolioScoreResponse'];

export interface CompanyAggEntry {
  companyId: number;
  count: number;
  max: string | null;
}

const buildCompanyFilter = (supplierIds: ReadonlyArray<number>) =>
  `companies.id:(${supplierIds.join(' or ')})`;

const flattenAgg = (aggs: AggregationResult[][] | null | undefined): CompanyAggEntry[] => {
  const bucket = aggs?.[0] ?? [];
  return bucket
    .map((row) => {
      const companyId = Number(row.key?.[0]);
      if (!Number.isFinite(companyId)) return null;
      const rawMax = (row as Record<string, unknown>).max;
      const max = typeof rawMax === 'string' ? rawMax : null;
      return { companyId, count: row.count, max };
    })
    .filter((entry): entry is CompanyAggEntry => entry !== null);
};

export const useMonitoringCompany = (clientId: number | null) =>
  useQuery({
    queryKey:
      clientId != null ? supplychainKeys.monitoringOverview(clientId) : ['supplychain-noop'],
    enabled: clientId != null,
    queryFn: async () => {
      const data = await openApiFetch('/catalog/companies/{resource_id}', {
        params: {
          path: { resource_id: clientId! },
          query: {
            include: [
              'suppliers.supplier_state',
              'suppliers.industries',
              'suppliers.technologies_provided',
            ],
          },
        },
      });

      return data;
    },
  });

/**
 * The customer's Portfolio Risk Score: a criticality-weighted
 * mean of their eligible supplier composites on the shared 0–1000 scale, plus
 * the Critical Exposure flag and coverage counts. Recomputed live on read (no
 * `as_of`, so the default "now" applies), so the number decays over time.
 */
export const useMonitoringPortfolioScore = (clientId: number | null) =>
  useQuery({
    queryKey:
      clientId != null ? supplychainKeys.monitoringPortfolioScore(clientId) : ['supplychain-noop'],
    enabled: clientId != null,
    queryFn: async () => {
      const data = await openApiFetch('/catalog/companies/{client_id}/risk_score_combined', {
        params: {
          path: { client_id: clientId! },
        },
      });

      return data as PortfolioScoreResponse;
    },
  });

export const useMonitoringEventsAgg = (
  clientId: number | null,
  supplierIds: ReadonlyArray<number>
) =>
  useQuery({
    queryKey: supplychainKeys.monitoringEventsAgg(clientId ?? -1, supplierIds),
    enabled: clientId != null && supplierIds.length > 0,
    queryFn: async () => {
      const data = await openApiFetch('/alert/events', {
        params: {
          query: {
            filter: [buildCompanyFilter(supplierIds), buildSupplyChainTypeFilter()],
            agg: ['companies.id max(last_seen)'],
            length: 0,
          },
        },
      });

      return flattenAgg(data?.aggs);
    },
  });

export const useMonitoringNewEventsAgg = (
  clientId: number | null,
  supplierIds: ReadonlyArray<number>
) =>
  useQuery({
    queryKey: supplychainKeys.monitoringNewEventsAgg(clientId ?? -1, supplierIds),
    enabled: clientId != null && supplierIds.length > 0,
    queryFn: async () => {
      // Supply-chain events only. Scope "new/active" by the active client's
      // client_state (MSSP-safe), matching findings-table filters via
      // buildClientStatusFilter(['new'], …).
      const newEventsFilter = buildClientStatusFilter(['new'], clientId);
      if (!newEventsFilter) return [];

      const data = await openApiFetch('/alert/events', {
        params: {
          query: {
            filter: [
              buildCompanyFilter(supplierIds),
              buildSupplyChainTypeFilter(),
              newEventsFilter,
            ],
            agg: ['companies.id'],
            length: 0,
          },
        },
      });

      return flattenAgg(data?.aggs);
    },
  });
